import React, { useEffect, useState } from 'react'
import { formatISO } from 'date-fns'

import { Spinner, useDimensions } from '@telavita-core/react-design-kit'

import metricsService, { IPersonsTotals } from 'services/metrics'
import { rangeDateFilterOptions } from 'settings/_filterSettings'

import { GraphicsCard } from 'components/GraphicsCard'
import {
  GraphicsDropdown,
  SelectedDate,
  getRangeDays,
  lastDay
} from 'components/GraphicsDropdown'

import {
  Loading,
  Content,
  Container,
  TotalWrapper,
  Total,
  Label,
  AnalyticsWrapper,
  AnalyticsItem,
  AnalyticsHeader,
  AnalyticsPercentage,
} from './styles'

interface Percentage {
  new: number
  recurring: number
}

export function PatientsCare(): JSX.Element {
  const initialOption = rangeDateFilterOptions[0]

  const { isMobile } = useDimensions()

  const [personsTotals, setPersonsTotals] = useState<IPersonsTotals>({
    new: 0,
    partial: 0,
    recurring: 0,
  })
  const [percentages, setPercentages] = useState<Percentage>({
    new: 0,
    recurring: 0,
  })
  const [loading, setLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState<SelectedDate>({
    initialDate: getRangeDays(initialOption.quantity).toString(),
    endDate: lastDay.toString(),
  })

  useEffect(() => {
    async function getPersonsTotals() {
      setLoading(true)
      const response = await metricsService.listPersonsTotals({
        initialDate: formatISO(new Date(selectedDate.initialDate)),
        endDate: formatISO(new Date(selectedDate.endDate)),
      })

      const isValueNan = (value: number) => isNaN(value) ? 0 : value
        
      const percentageNew = (response.new / response.partial) * 100
      const percentageRecurring = (response.recurring / response.partial) * 100

      const roundedPercentageNew = Math.round(isValueNan(percentageNew))
      const roundedPercentageRecurring = Math.round(isValueNan(percentageRecurring))

      setPersonsTotals(response)
      setPercentages({
        new: roundedPercentageNew,
        recurring: roundedPercentageRecurring,
      })
      setLoading(false)
    }

    void getPersonsTotals()
  }, [selectedDate])

  return (
    <GraphicsCard 
      id='sectionAppointments'
      title='Atendimentos'
      headerAction={
        <GraphicsDropdown 
          onSelectedDate={setSelectedDate}
          initialOption={initialOption}
        />
      }
    >
      <Container>
        {loading && (
          <Loading>
            <Spinner />
          </Loading>
        )}
        <Content
          isLoading={loading}
          isMobile={isMobile}
        >
          <TotalWrapper
            isMobile={isMobile}
          >
            <Total
              size={isMobile ? 35 : 45}
              lineHeight={isMobile ? 30 : 40}
            >
              {personsTotals.partial}
            </Total>
            <Label
              weight='bold'
              maxWidth={60}
              lineHeight={12}
            >
              Pacientes atendidos
            </Label>
          </TotalWrapper>
          <AnalyticsWrapper>
            <AnalyticsItem>
              <AnalyticsHeader>
                <Total
                  size={25}
                  lineHeight={29}
                >
                  {personsTotals.recurring}
                </Total>
                <AnalyticsPercentage>{`${percentages.recurring}%`}</AnalyticsPercentage>
              </AnalyticsHeader>
              <Label
                weight='normal'
                maxWidth={100}
                lineHeight={15}
              >
                <>Pacientes recorrentes<br />(2ª vez ou +)</>
              </Label>
            </AnalyticsItem>
            <AnalyticsItem>
              <AnalyticsHeader>
                <Total
                  size={25}
                  lineHeight={29}
                >
                  {personsTotals.new}
                </Total>
                <AnalyticsPercentage>{`${percentages.new}%`}</AnalyticsPercentage>
              </AnalyticsHeader>
              <Label
                weight='normal'
                maxWidth={70}
                lineHeight={15}
              >
                <>Pacientes novos<br />(1ª vez)</>
              </Label>
            </AnalyticsItem>
          </AnalyticsWrapper>
        </Content>
      </Container>
    </GraphicsCard>
  )
}